import { Modal as ModalModule, UI, Utils } from 'common';
import React, { useContext, useState } from 'react';
import ReprintModal from './ReprintModal.component';

const { Modal, ModalContext, ModalButtonActionType } = ModalModule;
const { WrappedCheckbox, Link } = UI;
const { Navigation } = Utils;

const UspsFraudInfoModal = ({ modalId }) => (
	<Modal
		modalId={modalId}
		className="usps-fraud-info-modal"
		header="USPS Mail Fraud Info"
		supressPrimaryButton
	>
		<div className="p-col-12">
			<p className="alert">
				In order to minimize fraud, Stamps.com has developed stringent tracking
				and auditing procedures with the U.S. Postal Service&reg;. The USPS&reg;
				may consider any misuse of U.S. mail to be a federal offense subject to
				severe penalties. We are required to report fraudulent credit requests
				for misprinted postage to the USPS.
			</p>
			<p className="certify-reprint-fraud-body">
				Any time that you make a claim for a postage refund or reprint, you are
				certifying that you have not used the postage and that the United States
				Postal Service has not performed any service related to this postage.
				You also understand that your refund claim will be verified by the
				United States Postal Service and/or its authorized representative.
			</p>
			<p>
				Filing false, fictitious or fraudulent refund requests for the return of
				U.S. postage is punishable under criminal laws of the United States,
				including 18 U.S.C. &sect;1001.
			</p>
		</div>
	</Modal>
);

const AlertText = ({ isNetStamps }) => (
	<p className="alert">
		You are attempting to reprint your last postage print.{' '}
		{isNetStamps ? (
			<>
				NetStamps can only be reprinted on the <b>same sheet and labels</b> as
				the original print.{' '}
			</>
		) : (
			''
		)}
		You will not be charged for the reprint. Reprint requires that you destroy
		any previous versions of this print.
	</p>
);

const BodyText = ({ isNetStamps }) => (
	<p
		className="certify-reprint-fraud-body"
		data-qa="certify-reprint-modal-text-field"
	>
		If you are unable to print to the same {!isNetStamps ? 'envelope, ' : ''}{' '}
		sheet or labels, or you need a modified version of this print, please cancel
		this reprint,{' '}
		<Link
			onClick={() => Navigation.navigateTo('requestPostageRefund')}
			dataqa="certify-reprint-request-refund-link"
		>
			request a refund
		</Link>{' '}
		for the misprinted {!isNetStamps ? 'envelope or ' : ''} labels, and start
		over with new information.
	</p>
);

const CertifyCheckbox = ({ onClickCertify, certifyReprint }) => {
	const { showModal } = useContext(ModalContext);

	const onClickUspsFraudInfo = () =>
		showModal({
			component: UspsFraudInfoModal,
		});

	return (
		<div className="certify-checkbox">
			<WrappedCheckbox
				inputId="certifyUspsReprint"
				name="hideWarning"
				value={certifyReprint}
				onChange={onClickCertify}
				checked={certifyReprint}
				htmlFor="certifyUspsReprint"
				dataqa="certify-reprint-message"
			>
				<p>
					I certify, under penalty of federal mail fraud, that the previously
					printed postage was never used.{' '}
					<Link
						onClick={onClickUspsFraudInfo}
						dataqa="certify-reprint-usps-mail-fraud-link"
					>
						USPS Mail Fraud Info
					</Link>
					.
				</p>
			</WrappedCheckbox>
		</div>
	);
};

const CertifyReprintModal = ({ modalId, data }) => {
	const [certifyReprint, setCertifyReprint] = useState(false);
	const { showModal, closeModal } = useContext(ModalContext);
	const { selectedPrint, labelDetails, setLabelDataForPrint } = data;
	const isNetStamps =
		selectedPrint?.printDetails?.printTypeName === 'NetStamps';

	const onClickCertify = ({ target: { checked } }) => {
		setCertifyReprint(checked);
	};

	const showReprintModal = () => {
		showModal({
			component: ReprintModal,
			data: {
				selectedPrint,
				labelDetails,
				setLabelDataForPrint,
				callback: () => closeModal(modalId),
			},
		});
	};

	const primaryButton = {
		type: ModalButtonActionType.Ok,
		text: 'Continue',
		disabled: !certifyReprint,
		onClick: showReprintModal,
		dataqa: 'certify-reprint-modal-continue-button',
	};

	return (
		<Modal
			modalId={modalId}
			className="certify-reprint-modal"
			header="Certify Reprint"
			primaryButton={primaryButton}
			dataqa="certify-reprint-modal-header"
			size="small"
		>
			<div>
				<AlertText isNetStamps={isNetStamps} />
				<BodyText isNetStamps={isNetStamps} />
				<CertifyCheckbox
					onClickCertify={onClickCertify}
					certifyReprint={certifyReprint}
				/>
			</div>
		</Modal>
	);
};

export default CertifyReprintModal;
